/**
 * Survey question configurations.
 * Each survey is an array of question objects passed to SurveyModal.
 *
 * Question types:
 *   scale — numbered buttons with endpoint labels (e.g., 1-5)
 *   radio — button group with text options
 *   text  — free text input
 *
 * ───────────────────────────────────────────────────────────────────────────
 * SINGLE-TASK CS 3114 DEPLOYMENTS  (669 Radix, 674-678)
 *
 * These use topic-parameterised questions built by getPreTaskQuestions() and
 * getPostTaskQuestions().  Two rules govern them:
 *
 *   1. `implementation_confidence` appears in BOTH the pre and post arrays with
 *      IDENTICAL wording.  The pre→post delta on that item is the study's
 *      self-perceived-competence measure.  Never reword one without the other.
 *
 *   2. Session 669 is FROZEN.  It is a live deployment whose responses are
 *      already keyed on `radix_familiarity`; changing its questions or keys
 *      mid-flight would split its own data.  The getters therefore return the
 *      legacy arrays for 669 and generated ones for every session after it.
 *
 * ANALYSIS NOTE: 669 stores familiarity under `radix_familiarity`; 674-678 store
 * it under `topic_familiarity`.  Pooling across deployments needs
 * coalesce(topic_familiarity, radix_familiarity).
 * ───────────────────────────────────────────────────────────────────────────
 */

export const PRE_TASK_QUESTIONS = [
  {
    key: 'bst_familiarity',
    label: 'How familiar are you with Binary Search Trees (BSTs)?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Not at all',
    maxLabel: 'Very familiar'
  },
  {
    key: 'implementation_confidence',
    label: 'How confident are you that you could complete a BST-related coding task right now without help?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Not at all',
    maxLabel: 'Very confident'
  }
]

export const PRE_QUIZ_QUESTIONS = [
  {
    key: 'bst_min_location',
    label: 'In a BST, where would you find the smallest value?',
    type: 'multiple_choice',
    options: ['At the root', 'At the leftmost node', 'At the rightmost node', 'It could be anywhere']
  },
  {
    key: 'inorder_traversal_order',
    label: 'In-order traversal visits nodes in which order?',
    type: 'multiple_choice',
    options: ['Root, then left, then right', 'Left, then root, then right', 'Left, then right, then root', 'Right, then root, then left']
  }
]

export const MID_SESSION_QUESTIONS = [
  {
    key: 'understanding',
    label: 'How well do you now understand Binary Search Trees (BSTs)?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Not at all',
    maxLabel: 'Very well'
  },
  {
    key: 'ai_helpfulness',
    label: 'How much did the AI help you learn?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Not at all',
    maxLabel: 'A lot'
  },
  {
    key: 'confidence_without_ai',
    label: 'How confident are you that you could do a similar task without AI?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Not at all',
    maxLabel: 'Very confident'
  }
]

export const POST_TASK_QUESTIONS = [
  {
    key: 'understanding',
    label: 'How well do you now understand Binary Search Trees (BSTs)?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Not at all',
    maxLabel: 'Very well'
  },
  {
    key: 'ai_preparation',
    label: 'Looking back, how much did the AI in Task 1 prepare you for Task 2?',
    type: 'scale',
    min: 1,
    max: 5,
    minLabel: 'Not at all',
    maxLabel: 'A lot',
    aiOnly: true
  },
  {
    key: 'used_external_resources',
    label: 'Did you use any external resources (e.g., ChatGPT, Google, notes) during Task 2?',
    type: 'radio',
    options: ['Yes', 'No', 'Prefer not to say'],
    subtitle: 'This is for research purposes only and will not affect you in any way. Honest answers help us improve our study.'
  },
  {
    key: 'external_resources_detail',
    label: 'What did you use?',
    type: 'text',
    placeholder: 'e.g., ChatGPT, Google, class notes...',
    optional: true,
    showIf: { key: 'used_external_resources', value: 'Yes' }
  },
  {
    key: 'feedback',
    label: 'Any feedback or suggestions about the experience?',
    type: 'text',
    placeholder: 'e.g., what worked well, what felt frustrating, anything you\'d change...',
    optional: true,
    subtitle: 'Optional — but we read every response and it helps us improve.'
  },
  {
    key: 'interview_interest',
    label: 'Would you be interested in a 15-minute paid interview ($10) to share your experience?',
    type: 'radio',
    options: ['Yes', 'No']
  }
]

/* ───────────────────────── 669 — FROZEN (live deployment) ───────────────────────── */

export const PRE_TASK_QUESTIONS_669 = [
  { key: 'radix_familiarity',
    label: 'How familiar are you with Radix Sort?',
    type: 'scale', min: 1, max: 5,
    minLabel: 'Not at all', maxLabel: 'Very familiar' },
  { key: 'implementation_confidence',
    label: 'How confident are you that you could implement Radix Sort right now without help?',
    type: 'scale', min: 1, max: 5,
    minLabel: 'Not at all', maxLabel: 'Very confident' },
]

export const POST_TASK_QUESTIONS_669 = [
  { key: 'understanding',
    label: 'How well do you now understand Radix Sort?',
    type: 'scale', min: 1, max: 5,
    minLabel: 'Not at all', maxLabel: 'Very well' },
  { key: 'implementation_confidence',
    label: 'How confident are you that you could implement Radix Sort right now without help?',
    type: 'scale', min: 1, max: 5,
    minLabel: 'Not at all', maxLabel: 'Very confident' },
  { key: 'used_external_resources',
    label: 'Did you use any external resources (e.g., ChatGPT, Google, notes) during this task?',
    type: 'radio', options: ['Yes', 'No', 'Prefer not to say'],
    subtitle: 'This is for research purposes only and will not affect your extra credit. Honest answers help us improve our study.' },
  { key: 'external_resources_detail',
    label: 'What did you use?',
    type: 'text', placeholder: 'e.g., ChatGPT, Google, class notes...',
    optional: true, showIf: { key: 'used_external_resources', value: 'Yes' } },
  { key: 'feedback',
    label: 'Any feedback or suggestions about the experience?',
    type: 'text', optional: true },
  { key: 'study_interest',
    label: 'Would you be interested in participating in a separate paid research study (~90 minutes, $30, virtual)?',
    type: 'radio', options: ['Yes, contact me', 'No thanks'] },
]

/* ──────────────── 674-678 — topic-parameterised single-task deployments ──────────────── */

const TASK_TOPICS = {
  674: {
    topic: 'hash tables and linear probing',
    implement: 'a hash table with linear probing'
  },
  675: {
    topic: 'depth-first search',
    implement: 'depth-first search on a graph'
  },
  676: {
    topic: 'general (non-binary) trees',
    implement: 'height and leaf-count methods for a general tree'
  },
  677: {
    topic: 'binary search trees',
    implement: 'a range-count function on a binary search tree'
  },
  678: {
    topic: 'binary search trees',
    implement: 'binary search tree validation'
  },
}

const FALLBACK_TOPIC = { topic: 'this topic', implement: 'this task' }

export const getPreTaskQuestions = (sessionId) => {
  const id = Number(sessionId)
  if (id === 669) return PRE_TASK_QUESTIONS_669          // frozen — live deployment

  const { topic, implement } = TASK_TOPICS[id] || FALLBACK_TOPIC
  return [
    { key: 'topic_familiarity',
      label: `How familiar are you with ${topic}?`,
      type: 'scale', min: 1, max: 5,
      minLabel: 'Not at all', maxLabel: 'Very familiar' },
    { key: 'implementation_confidence',
      // VERBATIM PAIR — must match the post-task item word for word.
      label: `How confident are you that you could implement ${implement} right now without help?`,
      type: 'scale', min: 1, max: 5,
      minLabel: 'Not at all', maxLabel: 'Very confident' },
  ]
}

export const getPostTaskQuestions = (sessionId) => {
  const id = Number(sessionId)
  if (id === 669) return POST_TASK_QUESTIONS_669         // frozen — live deployment

  const { topic, implement } = TASK_TOPICS[id] || FALLBACK_TOPIC
  return [
    { key: 'understanding',
      label: `How well do you now understand ${topic}?`,
      type: 'scale', min: 1, max: 5,
      minLabel: 'Not at all', maxLabel: 'Very well' },
    { key: 'implementation_confidence',
      // VERBATIM PAIR — must match the pre-task item word for word.
      label: `How confident are you that you could implement ${implement} right now without help?`,
      type: 'scale', min: 1, max: 5,
      minLabel: 'Not at all', maxLabel: 'Very confident' },
    { key: 'used_external_resources',
      label: 'Did you use any external resources (e.g., ChatGPT, Google, notes) during this task?',
      type: 'radio', options: ['Yes', 'No', 'Prefer not to say'],
      subtitle: 'This is for research purposes only and will not affect your extra credit. Honest answers help us improve our study.' },
    { key: 'external_resources_detail',
      label: 'What did you use?',
      type: 'text', placeholder: 'e.g., ChatGPT, Google, class notes...',
      optional: true, showIf: { key: 'used_external_resources', value: 'Yes' } },
    { key: 'feedback',
      label: 'Any feedback or suggestions about the experience?',
      type: 'text', optional: true },
    { key: 'study_interest',
      label: 'Would you be interested in participating in a separate paid research study (~90 minutes, $30, virtual)?',
      type: 'radio', options: ['Yes, contact me', 'No thanks'] },
  ]
}